import { Shield, ShieldCheck, Clock3 } from 'lucide-react';

// Operator-surface composite — refund-window indicator for Plus buyers.
// Composes the refund-eligibility logic from Convex `subscription_state.refund_eligible_until`
// with the `build_brief_confirmation_accepted_at` cutoff to surface a clear single status.

export interface RefundWindowIndicatorProps {
  refundEligibleUntil?: string | null;
  buildBriefConfirmationAcceptedAt?: string | null;
  coreAccessUntil?: string | null;
  now?: Date;
  // For Plus, includes 12 months of Core. After window expires, access continues.
  planLabel?: string;
  className?: string;
}

interface ComputedStatus {
  state: 'within_window' | 'cutoff_by_bbc' | 'expired_by_time' | 'unavailable';
  daysRemaining: number | null;
  effectiveCutoff: Date | null;
  cutoffReason: 'time' | 'bbc';
}

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function computeStatus({
  refundEligibleUntil,
  buildBriefConfirmationAcceptedAt,
  now,
}: Pick<
  RefundWindowIndicatorProps,
  'refundEligibleUntil' | 'buildBriefConfirmationAcceptedAt' | 'now'
>): ComputedStatus {
  const nowDate = now ?? new Date();
  const timeCutoff = parseDate(refundEligibleUntil);
  const bbcCutoff = parseDate(buildBriefConfirmationAcceptedAt);

  if (!timeCutoff && !bbcCutoff) {
    return { state: 'unavailable', daysRemaining: null, effectiveCutoff: null, cutoffReason: 'time' };
  }

  // Effective cutoff: earlier of time-cutoff and bbc-cutoff.
  let effective: Date | null = null;
  let reason: 'time' | 'bbc' = 'time';
  if (timeCutoff && bbcCutoff) {
    if (bbcCutoff.getTime() <= timeCutoff.getTime()) {
      effective = bbcCutoff;
      reason = 'bbc';
    } else {
      effective = timeCutoff;
      reason = 'time';
    }
  } else if (timeCutoff) {
    effective = timeCutoff;
    reason = 'time';
  } else if (bbcCutoff) {
    effective = bbcCutoff;
    reason = 'bbc';
  }

  if (!effective) {
    return { state: 'unavailable', daysRemaining: null, effectiveCutoff: null, cutoffReason: 'time' };
  }

  const diffMs = effective.getTime() - nowDate.getTime();
  if (diffMs <= 0) {
    return {
      state: reason === 'bbc' ? 'cutoff_by_bbc' : 'expired_by_time',
      daysRemaining: 0,
      effectiveCutoff: effective,
      cutoffReason: reason,
    };
  }
  const daysRemaining = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  return {
    state: 'within_window',
    daysRemaining,
    effectiveCutoff: effective,
    cutoffReason: reason,
  };
}

function formatCoreThrough(coreAccessUntil?: string | null): string | null {
  const d = parseDate(coreAccessUntil);
  if (!d) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function RefundWindowIndicator({
  refundEligibleUntil,
  buildBriefConfirmationAcceptedAt,
  coreAccessUntil,
  now,
  planLabel = 'Plus',
  className,
}: RefundWindowIndicatorProps) {
  const status = computeStatus({ refundEligibleUntil, buildBriefConfirmationAcceptedAt, now });
  const coreThrough = formatCoreThrough(coreAccessUntil);

  if (status.state === 'unavailable') return null;

  const isOpen = status.state === 'within_window';
  const Icon = isOpen ? Shield : ShieldCheck;
  const accent = isOpen ? 'border-fd-teal/40 bg-fd-teal-soft' : 'border-fd-border bg-fd-bg';

  let headline: string;
  let detail: string;
  if (isOpen) {
    const days = status.daysRemaining ?? 0;
    headline = `Refund window open · ${days} day${days === 1 ? '' : 's'} left`;
    detail = `Closes when you accept the Build Brief Confirmation, or after the 14-day window — whichever lands first. ${planLabel} access continues either way; refund only revokes inside the window.`;
  } else if (status.state === 'cutoff_by_bbc') {
    headline = 'Refund window closed · Build Brief Confirmation accepted';
    detail = `Build Brief Confirmation is the cutoff. Your ${planLabel} access continues${
      coreThrough ? ` and Core membership runs through ${coreThrough}.` : '.'
    } Refunds outside the window preserve current access; only future renewals are affected.`;
  } else {
    headline = 'Refund window closed · 14-day window passed';
    detail = `Your ${planLabel} access continues${
      coreThrough ? ` and Core membership runs through ${coreThrough}.` : '.'
    } Refunds outside the window preserve current access; only future renewals are affected.`;
  }

  return (
    <div
      role="status"
      className={[
        'flex items-start gap-3 rounded-[10px] border p-3.5',
        accent,
        className ?? '',
      ].join(' ')}
    >
      <span
        className={[
          'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-[4px] border',
          isOpen ? 'border-fd-teal/50 bg-fd-paper text-fd-teal' : 'border-fd-border bg-fd-paper text-fd-text-muted',
        ].join(' ')}
      >
        <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
      </span>
      <div className="min-w-0">
        <p className="font-geist-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-fd-text-subtle">
          Refund policy
        </p>
        <p className="mt-0.5 font-geist text-[13.5px] font-medium leading-[1.35] text-fd-text [text-wrap:balance]">
          {headline}
        </p>
        <p className="mt-1 text-[12.5px] leading-[1.5] text-fd-text-muted">
          {detail}
        </p>
        {isOpen && status.effectiveCutoff ? (
          <p className="mt-1.5 inline-flex items-center gap-1 font-geist-mono text-[11px] text-fd-text-subtle">
            <Clock3 className="h-3 w-3" />
            Closes{' '}
            {status.effectiveCutoff.toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </p>
        ) : null}
      </div>
    </div>
  );
}
